// Guacamole hook for Copilot: screenshot capture + input injection.
// Denver's Guacamole runs inside an iframe with an Angular app; we reach the
// underlying Guacamole.Client through the iframe's Angular injector — the same
// pattern used by resize.js and shareScreen.js.

import { MAX_LONG_EDGE, scaleActionCoord } from "./coordinateScaling";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// X11 keysym constants for special keys.
const KEY_MAP = {
  ctrl: 0xFFE3,
  control: 0xFFE3,
  alt: 0xFFE9,
  shift: 0xFFE1,
  win: 0xFFEB,
  super: 0xFFEB,
  meta: 0xFFEB,
  enter: 0xFF0D,
  return: 0xFF0D,
  tab: 0xFF09,
  esc: 0xFF1B,
  escape: 0xFF1B,
  backspace: 0xFF08,
  delete: 0xFFFF,
  del: 0xFFFF,
  insert: 0xFF63,
  home: 0xFF50,
  end: 0xFF57,
  pageup: 0xFF55,
  pagedown: 0xFF56,
  up: 0xFF52,
  down: 0xFF54,
  left: 0xFF51,
  right: 0xFF53,
  f1: 0xFFBE, f2: 0xFFBF, f3: 0xFFC0, f4: 0xFFC1,
  f5: 0xFFC2, f6: 0xFFC3, f7: 0xFFC4, f8: 0xFFC5,
  f9: 0xFFC6, f10: 0xFFC7, f11: 0xFFC8, f12: 0xFFC9,
  space: 0x0020,
};

// Last mouse position in native px, keyed per Guacamole client. left_mouse_down/up
// may follow a mouse_move and carry no coordinate of their own (quickstart
// semantics), so we remember where that client's cursor last went. WeakMap so
// entries are GC'd with the client.
const _lastMousePos = new WeakMap();

/**
 * Retrieve the underlying Guacamole.Client from the active iframe.
 * Mirrors resize.js:77 — read it off the `.display` element's Angular scope.
 *
 * Do NOT use guacClientManager.getManagedClient(id): that helper unconditionally
 * calls ungroupManagedClient(id), which strips the client out of every
 * ManagedClientGroup and tears down the rendered tile. It's safe only inside
 * Guacamole's own routing.
 *
 * @param {HTMLIFrameElement} iframe - The active VM iframe.
 * @returns {Guacamole.Client|null}
 */
export function getGuacClient(iframe) {
  try {
    const display = iframe.contentDocument.querySelector(".display");
    if (!display) return null;
    const scope = iframe.contentWindow.angular.element(display).scope();
    return scope?.client?.client || null;
  } catch (_) {
    return null;
  }
}

/**
 * Push `text` into the active VM's clipboard via Guacamole's clipboard
 * stream protocol. Required because `navigator.clipboard.writeText()` only
 * updates the local host clipboard — Guacamole's bridge listens for `copy`
 * events to mirror host→VM, so an async-API write leaves the VM clipboard
 * stale and right-click → paste in the guest pulls nothing. Mirrors
 * `ManagedClient.setClipboard` from guacamole-client/.../ManagedClient.js.
 *
 * @param {HTMLIFrameElement} iframe - The active VM iframe.
 * @param {string} text - The text to write into the VM clipboard.
 * @returns {boolean} true if the push was issued; false if the Guacamole
 *   client wasn't reachable (silently ignored — the host clipboard still
 *   has the text and Ctrl+Shift+V keeps working).
 */
export function pushTextToVmClipboard(iframe, text) {
  if (!iframe || typeof text !== "string" || text.length === 0) return false;
  const client = getGuacClient(iframe);
  if (!client) return false;
  try {
    const Guacamole = iframe.contentWindow.Guacamole;
    if (!Guacamole?.StringWriter) return false;
    const stream = client.createClipboardStream("text/plain");
    const writer = new Guacamole.StringWriter(stream);
    writer.sendText(text);
    writer.sendEnd();
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Capture a screenshot from the Guacamole display canvas, downscaled so the
 * long edge does not exceed maxLongEdge. Returns a JPEG Blob.
 * Based on prototype lines ~464-525.
 *
 * @param {HTMLIFrameElement} iframe - The active VM iframe.
 * @param {{ scaledWidth: number, scaledHeight: number }} scaledDimensions
 *   Hint from getScale() — used as the starting target; the function clamps
 *   further if the hint already exceeds maxLongEdge.
 * @param {number} [maxLongEdge=MAX_LONG_EDGE] - Long-edge pixel cap, threaded
 *   from the session's recommended_max_long_edge (§12).
 * @returns {Promise<Blob>}
 */
export async function captureScreenshot(iframe, { scaledWidth, scaledHeight }, maxLongEdge = MAX_LONG_EDGE) {
  const guacClient = getGuacClient(iframe);
  if (!guacClient) throw new Error("copilot: Guacamole client not available");

  const display = guacClient.getDisplay();
  const sourceCanvas = display.getDefaultLayer().getCanvas();
  const rawWidth = sourceCanvas.width;
  const rawHeight = sourceCanvas.height;

  // Downscale to ≤maxLongEdge per spec § 8.
  const longEdge = Math.max(rawWidth, rawHeight);
  let outWidth = scaledWidth;
  let outHeight = scaledHeight;
  if (longEdge > maxLongEdge) {
    const factor = maxLongEdge / longEdge;
    outWidth = Math.round(rawWidth * factor);
    outHeight = Math.round(rawHeight * factor);
  }

  // OffscreenCanvas is available in all modern browsers and Electron; it
  // doesn't require DOM access and avoids the need for document.createElement.
  const canvas = new OffscreenCanvas(outWidth, outHeight);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(sourceCanvas, 0, 0, rawWidth, rawHeight, 0, 0, outWidth, outHeight);
  return canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });
}

// Cap on the encoded zoom image so a large region can't blow the request/DB
// packet limit. Oversized crops re-encode at lower quality.
const ZOOM_MAX_BYTES = 1_500_000;

/**
 * Crop a region of the current Guacamole frame at native resolution and return
 * a size-capped JPEG Blob. `region` is [x0,y0,x1,y1] in model (scaled) space;
 * it is converted to native pixels via the scaling factors before cropping.
 * Observation-only — does not inject input or wait for the framebuffer.
 *
 * @param {HTMLIFrameElement} iframe
 * @param {[number,number,number,number]} region - [x0,y0,x1,y1] model-space.
 * @param {{ factorX: number, factorY: number }} scaling - getScale() result.
 * @returns {Promise<Blob>}
 */
export async function captureZoomRegion(iframe, region, scaling) {
  const guacClient = getGuacClient(iframe);
  if (!guacClient) throw new Error("copilot: Guacamole client not available");
  const [x0, y0, x1, y1] = region;
  const nx0 = Math.round(x0 * scaling.factorX);
  const ny0 = Math.round(y0 * scaling.factorY);
  const nw = Math.max(1, Math.round((x1 - x0) * scaling.factorX));
  const nh = Math.max(1, Math.round((y1 - y0) * scaling.factorY));

  const source = guacClient.getDisplay().getDefaultLayer().getCanvas();

  // Encode the crop at a given output scale + JPEG quality.
  const encode = (scale, quality) => {
    const w = Math.max(1, Math.round(nw * scale));
    const h = Math.max(1, Math.round(nh * scale));
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(source, nx0, ny0, nw, nh, 0, 0, w, h);
    return canvas.convertToBlob({ type: "image/jpeg", quality });
  };

  // Progressively lower quality, then downscale, until the encoded JPEG fits
  // within ZOOM_MAX_BYTES. The cap must actually hold — an oversized crop would
  // blow the request/DB packet limit. Each step strictly shrinks the output, so
  // this converges well under the cap rather than emitting an oversized blob.
  const steps = [
    [1, 0.85], [1, 0.5], [1, 0.35],
    [0.75, 0.5], [0.5, 0.5], [0.35, 0.4], [0.25, 0.35],
  ];
  let blob;
  for (const [scale, quality] of steps) {
    blob = await encode(scale, quality);
    if (blob.size <= ZOOM_MAX_BYTES) break;
  }
  return blob;
}

// Framebuffer-settle tuning. The model observes the screen as a result of its
// actions, so the capture must wait until the remote screen has stopped
// repainting — otherwise it sees a half-drawn frame.
//   graceMs   — minimum wait before a screen is eligible to be "settled", so we
//               never capture before the action's repaint has had a chance to start.
//   quietMs   — how long the framebuffer must be free of `sync` frames to count
//               as settled.
//   timeoutMs — hard cap: spinners/video never go quiet, so capture anyway.
//   pollMs    — how often to re-check the quiet/timeout conditions.
const SETTLE_DEFAULTS = { graceMs: 150, quietMs: 300, timeoutMs: 2500, pollMs: 50 };

/**
 * Resolve once the Guacamole framebuffer has settled after an action — i.e.
 * it has gone `quietMs` without a `sync` frame (but never before `graceMs`),
 * or `timeoutMs` elapses, whichever comes first. This is the event-driven
 * replacement for fixed per-action wait times: it captures as soon as the
 * screen stops changing, and caps the wait when it never does.
 *
 * The Guacamole client exposes a single `onsync` callback (not addEventListener),
 * and the webconnect Angular app installs its own handler for thumbnailing. We
 * chain ours in front of the existing one and restore it on resolve so we never
 * clobber the app's bookkeeping.
 *
 * @param {HTMLIFrameElement} iframe - The active VM iframe.
 * @param {{graceMs?: number, quietMs?: number, timeoutMs?: number, pollMs?: number}} [opts]
 * @returns {Promise<void>}
 */
export function waitForFramebufferSettle(iframe, opts = {}) {
  const { graceMs, quietMs, timeoutMs, pollMs } = { ...SETTLE_DEFAULTS, ...opts };
  const client = getGuacClient(iframe);
  // No client to listen on — fall back to a fixed grace sleep so the caller
  // still gets *some* settle delay before capturing.
  if (!client) return sleep(graceMs);

  return new Promise((resolve) => {
    const start = Date.now();
    let lastSync = start;

    const prevOnSync = client.onsync;
    const chained = function (timestamp, frames) {
      if (typeof prevOnSync === "function") {
        try { prevOnSync.call(this, timestamp, frames); } catch (_) { /* app handler is best-effort */ }
      }
      lastSync = Date.now();
    };
    client.onsync = chained;

    const timer = setInterval(() => {
      const now = Date.now();
      const elapsed = now - start;
      const sinceSync = now - lastSync;
      if ((elapsed >= graceMs && sinceSync >= quietMs) || elapsed >= timeoutMs) {
        clearInterval(timer);
        // Restore only if nothing else replaced our handler in the interim.
        if (client.onsync === chained) client.onsync = prevOnSync;
        resolve();
      }
    }, pollMs);
  });
}

/**
 * Inject a universal action shape into the Guacamole session via the iframe.
 * Translates to Guacamole.Client.sendMouseState / sendKeyEvent calls.
 * Per spec § 15.1 action set.
 *
 * @param {HTMLIFrameElement} iframe - The active VM iframe.
 * @param {object} action - Universal action object from the /turn response.
 * @param {{ scale: number }} scaling - Result of getScale().
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function injectAction(iframe, action, scaling) {
  const guacClient = getGuacClient(iframe);
  if (!guacClient) return { ok: false, error: "guac_client_unavailable" };

  // Guacamole.Mouse.State accepts legacy positional args: (x, y, left, middle, right, up, down)
  const mouseState = (x, y, left = false, middle = false, right = false, up = false, down = false) => {
    const Guacamole = iframe.contentWindow.Guacamole;
    return new Guacamole.Mouse.State(x, y, left, middle, right, up, down);
  };

  // Modifier keys (shift/ctrl/alt/super) held down for the duration of a
  // click or scroll, per spec § 4. Released in reverse order. A "+"-joined
  // spec (e.g. "ctrl+shift") presses each in sequence.
  const pressModifiers = (spec) => {
    if (!spec) return [];
    const codes = spec.split("+").map((p) => {
      const k = p.trim().toLowerCase();
      return KEY_MAP[k] ?? k.charCodeAt(0);
    });
    for (const c of codes) guacClient.sendKeyEvent(1, c);
    return codes;
  };
  const releaseModifiers = (codes) => {
    for (const c of [...codes].reverse()) guacClient.sendKeyEvent(0, c);
  };

  // Single click: hover → button-down → 40ms held → button-up.
  // Mid-action sleeps mirror prototype values; button-held interval is the one
  // that matters most over Guacamole's network latency.
  const doClick = async (x, y, flags, clicks = 1, modifierSpec = null) => {
    const mods = pressModifiers(modifierSpec);
    for (let i = 0; i < clicks; i++) {
      guacClient.sendMouseState(mouseState(x, y));
      await sleep(15);
      guacClient.sendMouseState(mouseState(x, y, flags.left, flags.middle, flags.right));
      await sleep(40);
      guacClient.sendMouseState(mouseState(x, y));
      if (i < clicks - 1) await sleep(80);
    }
    releaseModifiers(mods);
  };

  switch (action.type) {
    case "left_click": {
      const [x, y] = scaleActionCoord(action.coordinate, scaling);
      await doClick(x, y, { left: true }, 1, action.text);
      return { ok: true };
    }

    case "right_click": {
      const [x, y] = scaleActionCoord(action.coordinate, scaling);
      await doClick(x, y, { right: true }, 1, action.text);
      return { ok: true };
    }

    case "middle_click": {
      const [x, y] = scaleActionCoord(action.coordinate, scaling);
      await doClick(x, y, { middle: true }, 1, action.text);
      return { ok: true };
    }

    case "double_click": {
      const [x, y] = scaleActionCoord(action.coordinate, scaling);
      await doClick(x, y, { left: true }, 2, action.text);
      return { ok: true };
    }

    case "triple_click": {
      const [x, y] = scaleActionCoord(action.coordinate, scaling);
      await doClick(x, y, { left: true }, 3, action.text);
      return { ok: true };
    }

    case "mouse_move": {
      const [x, y] = scaleActionCoord(action.coordinate, scaling);
      _lastMousePos.set(guacClient, [x, y]);
      guacClient.sendMouseState(mouseState(x, y));
      return { ok: true };
    }

    case "left_click_drag": {
      const [sx, sy] = scaleActionCoord(action.start_coordinate, scaling);
      const [ex, ey] = scaleActionCoord(action.coordinate, scaling);
      guacClient.sendMouseState(mouseState(sx, sy));
      await sleep(20);
      guacClient.sendMouseState(mouseState(sx, sy, true));
      await sleep(40);
      guacClient.sendMouseState(mouseState(ex, ey, true));
      guacClient.sendMouseState(mouseState(ex, ey));
      return { ok: true };
    }

    case "type": {
      // Whitespace control characters need their X11 keysyms — sending the
      // raw ASCII control code (\n=0x0A, \t=0x09, \r=0x0D) produces nothing
      // visible in most apps. Multi-line text typed without this remap
      // collapses into a single line on the VM.
      const TYPE_KEYSYM = { "\n": 0xFF0D, "\r": 0xFF0D, "\t": 0xFF09 };
      // sendKeyEvent just enqueues an instruction on the Guacamole tunnel;
      // Windows/RDP only requires press-before-release ordering, not a
      // human-scale press-hold duration. Yield every YIELD_EVERY chars so
      // multi-KB strings don't starve the renderer.
      const YIELD_EVERY = 32;
      let i = 0;
      for (const char of action.text) {
        const code = TYPE_KEYSYM[char] ?? char.charCodeAt(0);
        guacClient.sendKeyEvent(1, code);
        guacClient.sendKeyEvent(0, code);
        if (++i % YIELD_EVERY === 0) await sleep(0);
      }
      return { ok: true };
    }

    case "type_at": {
      // Click at the target coordinate to focus, optionally clear the
      // existing field via triple-click+overwrite, type the text, optionally
      // press Enter. Mirrors Gemini's predefined type_text_at semantics.
      const [x, y] = scaleActionCoord(action.coordinate, scaling);
      if (action.clear_before_typing) {
        // Triple-click selects field contents; subsequent typing replaces them.
        await doClick(x, y, { left: true });
        await sleep(40);
        await doClick(x, y, { left: true });
        await sleep(40);
        await doClick(x, y, { left: true });
      } else {
        await doClick(x, y, { left: true });
      }
      // Let the click register and the field take focus before typing —
      // event-driven so we wait exactly as long as the focus repaint takes.
      await waitForFramebufferSettle(iframe);
      const TYPE_KEYSYM_AT = { "\n": 0xFF0D, "\r": 0xFF0D, "\t": 0xFF09 };
      const YIELD_EVERY = 32;
      let n = 0;
      for (const char of (action.text || "")) {
        const code = TYPE_KEYSYM_AT[char] ?? char.charCodeAt(0);
        guacClient.sendKeyEvent(1, code);
        guacClient.sendKeyEvent(0, code);
        if (++n % YIELD_EVERY === 0) await sleep(0);
      }
      if (action.press_enter) {
        const ENTER = 0xFF0D;
        guacClient.sendKeyEvent(1, ENTER);
        guacClient.sendKeyEvent(0, ENTER);
      }
      return { ok: true };
    }

    case "key": {
      const parts = action.text.split("+").map((p) => p.trim().toLowerCase());
      const codes = parts.map((p) => KEY_MAP[p] ?? p.charCodeAt(0));
      for (const c of codes) { guacClient.sendKeyEvent(1, c); await sleep(8); }
      await sleep(30);
      for (const c of [...codes].reverse()) { guacClient.sendKeyEvent(0, c); await sleep(8); }
      return { ok: true };
    }

    case "scroll": {
      const [x, y] = scaleActionCoord(action.coordinate, scaling);
      const scrollUp = action.scroll_direction === "up";
      const scrollDown = action.scroll_direction === "down";
      const amount = action.scroll_amount || 1;
      // Pre-move: park the cursor at (x, y) and let the VM settle
      // before any wheel events fire. Without this, the wheel button
      // press reaches the VM before the cursor-tracking has updated,
      // and Windows dispatches the wheel to whatever was under the
      // user's real cursor — the scroll "misses" its target.
      const mods = pressModifiers(action.text);
      guacClient.sendMouseState(mouseState(x, y));
      await sleep(50);
      for (let i = 0; i < amount; i++) {
        // Re-assert position before each pulse in case user mouse
        // input has bled through between iterations.
        guacClient.sendMouseState(mouseState(x, y));
        await sleep(10);
        guacClient.sendMouseState(mouseState(x, y, false, false, false, scrollUp, scrollDown));
        await sleep(20);
        guacClient.sendMouseState(mouseState(x, y));
        await sleep(20);
      }
      releaseModifiers(mods);
      return { ok: true };
    }

    case "wait": {
      const ms = Math.min((action.duration || 1) * 1000, 5000);
      await sleep(ms);
      return { ok: true };
    }

    case "screenshot":
      // No-op: the executor captures a fresh frame after every action batch,
      // so the model never needs to request one. Tolerated if it does anyway.
      return { ok: true };

    case "left_mouse_down": {
      if (action.coordinate) _lastMousePos.set(guacClient, scaleActionCoord(action.coordinate, scaling));
      const [x, y] = _lastMousePos.get(guacClient) || [0, 0];
      guacClient.sendMouseState(mouseState(x, y, true));
      return { ok: true };
    }

    case "left_mouse_up": {
      if (action.coordinate) _lastMousePos.set(guacClient, scaleActionCoord(action.coordinate, scaling));
      const [x, y] = _lastMousePos.get(guacClient) || [0, 0];
      guacClient.sendMouseState(mouseState(x, y));
      return { ok: true };
    }

    case "hold_key": {
      const parts = action.text.split("+").map((p) => p.trim().toLowerCase());
      const codes = parts.map((p) => KEY_MAP[p] ?? p.charCodeAt(0));
      const holdMs = Math.min((action.duration || 1) * 1000, 5000);
      for (const c of codes) { guacClient.sendKeyEvent(1, c); await sleep(8); }
      await sleep(holdMs);
      for (const c of [...codes].reverse()) { guacClient.sendKeyEvent(0, c); await sleep(8); }
      return { ok: true };
    }

    default:
      return { ok: false, error: `unknown_action_type:${action.type}` };
  }
}
